<script setup>
/**
 * GiftStar: the star of the two of you (gift room redo, 2026-10-29,
 * Claude outputs/gift-room-2026-10-29/DESIGN.md "Step 6").
 *
 * A radar drawn as a five-pointed star: each arm is one of the five built
 * scores (attraction, emotional, communication, harmony, commitment) from a
 * 25% floor to full, so every pair's star is its own shape. The arms grow from
 * the centre in turn (140 ms apart, about 0.9 s each), then the labels fade
 * in; with reduced motion it is drawn at once. Each arm is a button: a tap (or
 * Enter / Space) brightens it, dims the others slightly and shows its one
 * plain line under the star ("Communication · You get there in the end…").
 * The lines come from the server (`star_words.ARM_LINES`, one per dimension
 * and band); no number is ever shown.
 */
import { computed, ref } from 'vue'
import { reducedMotionNow } from '@/composables/useReducedMotion'
import { starShape } from '@/utils/gift-steps'

const props = defineProps({
  // [{ key, label, value, band, line }] in the built order (our_stars `arms`).
  arms: { type: Array, default: () => [] },
  size: { type: Number, default: 260 },
})
const emit = defineEmits(['arm'])

const reduced = reducedMotionNow()
const uid = `gs${Math.random().toString(36).slice(2, 8)}`
const shape = computed(() => starShape(props.arms, { size: props.size }))
const on = ref(-1)
const shown = computed(() => (on.value >= 0 ? shape.value.arms[on.value] : null))

function pick(i) {
  on.value = i
  const arm = shape.value.arms[i]
  if (arm) emit('arm', arm.key)
}
function onKey(e, i) {
  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(i) }
}
const fmt = (n) => Number(n).toFixed(1)
</script>

<template>
  <div class="gs" data-testid="gift-star">
    <svg v-if="shape.arms.length" class="gs-svg" :class="{ grow: !reduced }" :viewBox="`0 0 ${shape.vw} ${shape.vh}`"
         :width="shape.vw" :height="shape.vh" role="group" aria-label="The star of the two of you">
      <defs>
        <radialGradient :id="uid + 'f'" :cx="shape.cx" :cy="shape.cy" :r="shape.r" gradientUnits="userSpaceOnUse">
          <stop offset="0" stop-color="#fff1cf" /><stop offset=".6" stop-color="#f0d9a3" /><stop offset="1" stop-color="#c9a94e" />
        </radialGradient>
        <filter :id="uid + 'g'" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur :stdDeviation="size / 46" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <circle v-for="(g, k) in shape.guides" :key="'g' + k" :cx="shape.cx" :cy="shape.cy" :r="fmt(g)" fill="none"
              :stroke="k === 3 ? 'rgba(201,184,230,.3)' : 'rgba(201,184,230,.12)'" :stroke-dasharray="k === 3 ? '2 4' : '1 5'" />
      <g v-for="(a, i) in shape.arms" :key="a.key" class="gs-arm" :class="{ on: on === i, dim: on >= 0 && on !== i }"
         role="button" tabindex="0" :aria-label="a.label" :aria-pressed="on === i ? 'true' : 'false'" data-testid="star-arm"
         @click="pick(i)" @keydown="onKey($event, i)">
        <path class="petal" :d="a.path" :fill="`url(#${uid}f)`" :filter="`url(#${uid}g)`"
              :style="{ animationDelay: (i * 140) + 'ms', '--cx': shape.cx + 'px', '--cy': shape.cy + 'px' }" />
        <circle class="petal tip" :cx="fmt(a.tip[0])" :cy="fmt(a.tip[1])" :r="on === i ? 4 : 2.6"
                :style="{ animationDelay: (i * 140) + 'ms', '--cx': shape.cx + 'px', '--cy': shape.cy + 'px' }" />
        <text class="lab" :x="fmt(a.labelAt.x)" :y="fmt(a.labelAt.y)" :text-anchor="a.labelAt.anchor"
              :style="{ animationDelay: (900 + i * 120) + 'ms' }">{{ a.label }}</text>
        <circle class="hit" :cx="fmt(a.tip[0])" :cy="fmt(a.tip[1])" r="26" />
      </g>
      <circle :cx="shape.cx" :cy="shape.cy" r="3" fill="#2a1845" />
    </svg>
    <p class="gs-line" :class="{ hint: !shown }" aria-live="polite" data-testid="star-line">
      <template v-if="shown"><b>{{ shown.label }}</b> {{ shown.line }}</template>
      <template v-else>Tap a point of your star.</template>
    </p>
  </div>
</template>

<style scoped>
.gs { text-align: center; }
.gs-svg { display: block; margin: 4px auto 0; overflow: visible; max-width: 100%; height: auto; }
.gs-arm { cursor: pointer; outline: none; }
.gs-arm path { stroke: #fff1cf; stroke-width: 0.8; transition: opacity 0.2s, stroke 0.2s; }
.gs-arm .tip { fill: #fff6dc; }
.gs-arm.on path { stroke: #fff; stroke-width: 1.6; }
.gs-arm.dim path { opacity: 0.72; }
.gs-arm:focus-visible path { stroke: #fff; stroke-width: 1.6; }
.gs-arm .lab { font: 600 11px 'Letter Display', 'Cinzel', Georgia, serif; letter-spacing: 0.1em; fill: #e7c983;
  text-transform: uppercase; transition: fill 0.2s;
  /* A dark edge under each letter: the ring's dashes never run through a word. */
  paint-order: stroke; stroke: #24123a; stroke-width: 3px; stroke-linejoin: round; }
.gs-arm.on .lab, .gs-arm:focus-visible .lab { fill: #fff6dc; }
.gs-arm .hit { fill: transparent; }
.grow .petal { transform-box: view-box; transform-origin: var(--cx) var(--cy); transform: scale(0.05); opacity: 0;
  animation: gs-petal 900ms cubic-bezier(0.2, 0.9, 0.3, 1.2) forwards; }
.grow .lab { opacity: 0; animation: gs-fade 500ms ease forwards; }
@keyframes gs-petal { to { transform: none; opacity: 1; } }
@keyframes gs-fade { to { opacity: 1; } }
.gs-line { min-height: 50px; margin: 2px auto 10px; max-width: 460px; font: 500 16px/1.45 Inter, system-ui, sans-serif;
  color: #f3e8ff; }
.gs-line b { display: block; font: 600 10.5px 'Letter Display', 'Cinzel', Georgia, serif; letter-spacing: 0.2em;
  color: #e7c983; text-transform: uppercase; margin-bottom: 2px; }
.gs-line.hint { font: italic 500 15px 'Letter Serif', 'Cormorant Garamond', Georgia, serif; color: #9b86c9; }
@media (prefers-reduced-motion: reduce) {
  .grow .petal, .grow .lab { animation: none; opacity: 1; transform: none; }
}
</style>
